import { GameState, WorldPoint, BuildingBox } from '../domain/types.ts';
import { Camera } from './camera.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';

export class CanvasRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private camera: Camera;

  constructor(canvas: HTMLCanvasElement, camera: Camera) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D canvas context');
    this.ctx = ctx;
    this.camera = camera;
  }

  public resize(): void {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const rect = this.canvas.getBoundingClientRect();

    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);

    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.camera.resize(rect.width, rect.height);
  }

  public render(state: Readonly<GameState>): void {
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    // 1. Clear background
    this.ctx.fillStyle = GAME_CONFIG.render.colors.bg;
    this.ctx.fillRect(0, 0, width, height);

    // Grid lines for street atmosphere
    this.renderStreetGrid();

    // 1.5 Ranh giới cố định 100 mét
    this.renderBoundary();

    // 2. Render Roads & Sidewalks
    this.renderRoads(state);

    // 3. Render Chili Powder Traps
    this.renderChiliTraps(state);

    // 4. Render Buildings
    this.renderBuildings(state);

    // 5. Render Trees & Waste Baskets
    this.renderTrees(state);
    this.renderWasteBaskets(state);

    // 6. Render Surveillance Cones & Tea Stall Radar (Layer dưới nhân vật)
    this.renderSurveillanceZones(state);

    // 7. Render Poops & Footprints
    this.renderPoops(state);
    this.renderFootprints(state);

    // 7.5 Render Target Marker
    if (state.player.targetMove) {
      this.renderTargetMarker(state.player.targetMove);
    }

    // 8. Render Leash, Player & Dog
    this.renderLeash(state);
    this.renderPlayer(state);
    this.renderDog(state);

    // 9. Render NPCs (Inspector, Tea stall people)
    this.renderInspectors(state);
    this.renderTeaStall(state);

    // 9.5 Render Dummy Walker Rivals & Dogs
    this.renderDummyWalkers(state);
    this.renderChickenGameFx(state);
    this.renderSoundWaves(state);

    // 10. Render QTE Aim line (nếu đang căn lực sút)
    if (state.qteKick.active) {
      this.renderQTEAim(state);
    }
  }

  private renderTargetMarker(target: WorldPoint): void {
    const sc = this.camera.worldToScreen(target);
    const ctx = this.ctx;
    const pulse = (Math.sin(performance.now() / 150) + 1) / 2;

    ctx.beginPath();
    ctx.arc(sc.x, sc.y, 8 + pulse * 4, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(79, 195, 247, ${0.4 + pulse * 0.4})`;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🐾', sc.x, sc.y);
  }

  public renderVirtualJoystick(joystick?: {
    active: boolean;
    baseX: number;
    baseY: number;
    knobX: number;
    knobY: number;
    radius: number;
  }): void {
    if (!joystick || !joystick.active) return;
    const ctx = this.ctx;

    // Vòng tròn gốc Joystick
    ctx.beginPath();
    ctx.arc(joystick.baseX, joystick.baseY, joystick.radius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Núm vặn ngón cái
    ctx.beginPath();
    ctx.arc(joystick.knobX, joystick.knobY, 22, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(41, 182, 246, 0.7)';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }

  private renderStreetGrid(): void {
    const ctx = this.ctx;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.025)';
    ctx.lineWidth = 1;

    // Vẽ lưới ô vuông 10m co giãn theo bán kính bản đồ thực tế
    const extent = Math.ceil((this.camera.maxRadiusM || 100) / 10) * 10;
    const minMeters = -extent;
    const maxMeters = extent;
    const step = 10;

    for (let x = minMeters; x <= maxMeters; x += step) {
      const p1 = this.camera.worldToScreen({ x, y: minMeters });
      const p2 = this.camera.worldToScreen({ x, y: maxMeters });
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
    for (let y = minMeters; y <= maxMeters; y += step) {
      const p1 = this.camera.worldToScreen({ x: minMeters, y });
      const p2 = this.camera.worldToScreen({ x: maxMeters, y });
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
  }

  private renderBoundary(): void {
    const ctx = this.ctx;
    const centerSc = this.camera.worldToScreen({ x: 0, y: 0 });
    const r = this.camera.maxRadiusM * this.camera.zoom; // 100 mét chuẩn

    ctx.beginPath();
    ctx.arc(centerSc.x, centerSc.y, r, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255, 213, 79, 0.45)';
    ctx.lineWidth = 2.5;
    ctx.setLineDash([10, 8]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Tiêu đề ranh giới khu phố (co giãn lên đến 200m)
    ctx.font = 'bold 12px sans-serif';
    ctx.fillStyle = 'rgba(255, 213, 79, 0.85)';
    ctx.textAlign = 'center';
    ctx.fillText(`🚧 RANH GIỚI KHU PHỐ ${Math.round(this.camera.maxRadiusM)} MÉT 🚧`, centerSc.x, centerSc.y - r + 20);
  }

  private renderRoads(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    for (const road of state.roads) {
      if (road.points.length < 2) continue;

      // 1. Lớp vỉa hè (Sidewalk viền ngoài)
      ctx.beginPath();
      const pStart = this.camera.worldToScreen(road.points[0]!);
      ctx.moveTo(pStart.x, pStart.y);
      for (let i = 1; i < road.points.length; i++) {
        const pt = this.camera.worldToScreen(road.points[i]!);
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.strokeStyle = GAME_CONFIG.render.colors.sidewalk;
      ctx.lineWidth = (road.width + 4) * this.camera.zoom;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();

      // 2. Lòng đường (Asphalt)
      ctx.beginPath();
      ctx.moveTo(pStart.x, pStart.y);
      for (let i = 1; i < road.points.length; i++) {
        const pt = this.camera.worldToScreen(road.points[i]!);
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.strokeStyle = GAME_CONFIG.render.colors.road;
      ctx.lineWidth = road.width * this.camera.zoom;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();

      // 3. Vạch sơn tim đường màu vàng nhạt
      if (road.width >= 7) {
        ctx.beginPath();
        ctx.moveTo(pStart.x, pStart.y);
        for (let i = 1; i < road.points.length; i++) {
          const pt = this.camera.worldToScreen(road.points[i]!);
          ctx.lineTo(pt.x, pt.y);
        }
        ctx.strokeStyle = 'rgba(255, 213, 79, 0.4)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([8, 8]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
  }

  private renderChiliTraps(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    for (const trap of state.chiliTraps) {
      const sc = this.camera.worldToScreen({ x: trap.x, y: trap.y });
      const r = trap.radius * this.camera.zoom;

      // Vùng ớt đỏ nhạt
      ctx.beginPath();
      ctx.arc(sc.x, sc.y, r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(244, 67, 54, 0.2)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(244, 67, 54, 0.6)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Icon quả ớt cảnh báo
      ctx.font = `${Math.max(12, 14 * (this.camera.zoom / 4))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🌶️ Bẫy ớt bột', sc.x, sc.y);
    }
  }

  private renderBuildings(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const zoomRatio = this.camera.zoom / this.camera.baseZoom;
    const showBuildingNames = zoomRatio >= 1.25;

    const ambientList: BuildingBox[] = [];
    const poiList: BuildingBox[] = [];
    for (const b of state.buildings) {
      if (b.isAmbient) {
        ambientList.push(b);
      } else {
        poiList.push(b);
      }
    }

    // =========================================================================
    // 1. RENDER KHỐI NHÀ MỜ (CHỈ LÀM NỀN TẠO ẤN TƯỢNG NHƯ MẪU CŨ)
    // =========================================================================
    for (const b of ambientList) {
      const tl = this.camera.worldToScreen({ x: b.x, y: b.y });
      const w = b.width * this.camera.zoom;
      const h = b.height * this.camera.zoom;

      // Khối nhà chữ nhật phẳng màu tối mờ ảo
      ctx.fillStyle = 'rgba(18, 24, 36, 0.45)';
      ctx.fillRect(tl.x, tl.y, w, h);

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1;
      ctx.strokeRect(tl.x, tl.y, w, h);

      // Chấm sáng cửa sổ li ti như sao đêm trong ảnh mẫu cũ
      let seed = Math.abs(Math.sin(b.x * 12.9898 + b.y * 78.233) * 43758.5453);
      for (let i = 0; i < 4; i++) {
        seed = (seed * 9301 + 49297) % 233280;
        const rx = seed / 233280;
        seed = (seed * 9301 + 49297) % 233280;
        const ry = seed / 233280;

        const winX = tl.x + 3 + rx * Math.max(2, w - 6);
        const winY = tl.y + 3 + ry * Math.max(2, h - 6);
        ctx.fillStyle = 'rgba(255, 215, 0, 0.45)';
        ctx.fillRect(winX, winY, 2, 2);
      }
    }

    // =========================================================================
    // 2. RENDER KHỐI NHÀ CHÍNH (CHUẨN FORM 2D PHẲNG, VIỀN NEON RỰC RỠ NHƯ MẪU CŨ)
    // =========================================================================
    for (const b of poiList) {
      const tl = this.camera.worldToScreen({ x: b.x, y: b.y });
      const w = b.width * this.camera.zoom;
      const h = b.height * this.camera.zoom;

      // a. Nền khối nhà phẳng chuẩn mực
      ctx.fillStyle = b.hasCamera ? 'rgba(38, 20, 24, 0.95)' : 'rgba(16, 24, 38, 0.95)';
      ctx.fillRect(tl.x, tl.y, w, h);

      // b. Viền neon sắc nét: Đỏ cho Camera, Xanh Cyan cho nhà thường
      ctx.strokeStyle = b.hasCamera ? '#ef5350' : '#38bdf8';
      ctx.lineWidth = 2.0;
      ctx.strokeRect(tl.x, tl.y, w, h);

      // c. Icon đặc trưng của tòa nhà to bản, sắc nét ở chính giữa
      let buildingIcon = '🏠';
      if (b.id.includes('kimtai')) buildingIcon = '🏢';
      else if (b.id.includes('totruong')) buildingIcon = '🏢';
      else if (b.id.includes('taphoa')) buildingIcon = '🏪';
      else if (b.id.includes('ccmini')) buildingIcon = '🏢';
      else if (b.id.includes('chohoang')) buildingIcon = '🐕';
      else if (b.id.includes('so12')) buildingIcon = '🌶️';
      else if (b.id.includes('combinhdan')) buildingIcon = '🍚';

      const iconY = b.hasCamera ? tl.y + h / 2 - 4 : tl.y + h / 2;
      ctx.font = `${Math.max(16, Math.min(24, 18 * (this.camera.zoom / 1.5)))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(buildingIcon, tl.x + w / 2, iconY);

      // d. Tên nhà: Chỉ hiện khi zoom in (LOD)
      if (showBuildingNames) {
        const shortName = b.name.split('(')[0]!.trim();
        const fontSize = Math.max(9, Math.min(11, 10 * (this.camera.zoom / 1.5)));
        ctx.font = `600 ${fontSize}px sans-serif`;
        ctx.fillStyle = '#cfd8dc';
        ctx.fillText(shortName, tl.x + w / 2, tl.y + h / 2 + 10);
      }

      // e. Nhãn Camera 4K góc dưới như ảnh mẫu cũ
      if (b.hasCamera) {
        ctx.fillStyle = '#ef5350';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('🔴 CAM 4K', tl.x + w / 2, tl.y + h - 5);
      }
    }
  }

  private renderTrees(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    for (const tree of state.trees) {
      const sc = this.camera.worldToScreen({ x: tree.x, y: tree.y });
      const r = 1.8 * this.camera.zoom;

      // Tán cây bóng đổ
      ctx.beginPath();
      ctx.arc(sc.x + 2, sc.y + 2, r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fill();

      // Tán cây xanh mát
      ctx.beginPath();
      ctx.arc(sc.x, sc.y, r, 0, Math.PI * 2);
      ctx.fillStyle = '#388e3c';
      ctx.fill();
      ctx.strokeStyle = '#66bb6a';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.font = `${Math.max(12, 14 * (this.camera.zoom / 4))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🌳', sc.x, sc.y);
    }
  }

  private renderWasteBaskets(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const zoomRatio = this.camera.zoom / this.camera.baseZoom;
    for (const wb of state.wasteBaskets) {
      const sc = this.camera.worldToScreen({ x: wb.x, y: wb.y });
      ctx.font = `${Math.max(14, 16 * (this.camera.zoom / 4))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🗑️', sc.x, sc.y);

      // Nhãn chữ thùng rác chỉ hiện khi zoom in (>= 1.35x)
      if (zoomRatio >= 1.35) {
        ctx.font = '9px sans-serif';
        ctx.fillStyle = '#4dd0e1';
        ctx.fillText('Thùng rác', sc.x, sc.y + 12);
      }
    }
  }

  private renderSurveillanceZones(state: Readonly<GameState>): void {
    const ctx = this.ctx;

    // 1. Quán Trà Đá: Vòng tròn radar 360 độ chạy bằng cơm
    for (const stall of state.teaStalls) {
      const sc = this.camera.worldToScreen({ x: stall.x, y: stall.y });
      const r = stall.radius * this.camera.zoom;

      ctx.beginPath();
      ctx.arc(sc.x, sc.y, r, 0, Math.PI * 2);
      ctx.fillStyle = GAME_CONFIG.render.colors.teaStallRadar;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 112, 67, 0.4)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // 2. Camera An Ninh 4K: Nón tầm nhìn với đèn LED nhấp nháy
    for (const cam of state.cameras) {
      const sc = this.camera.worldToScreen({ x: cam.x, y: cam.y });
      const r = cam.range * this.camera.zoom;
      const startAngle = cam.facingAngle - cam.fov / 2;
      const endAngle = cam.facingAngle + cam.fov / 2;

      ctx.beginPath();
      ctx.moveTo(sc.x, sc.y);
      ctx.arc(sc.x, sc.y, r, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = 'rgba(244, 67, 54, 0.14)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(244, 67, 54, 0.5)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Đèn LED đỏ nhấp nháy trên ống kính
      ctx.beginPath();
      ctx.arc(sc.x, sc.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = cam.isBlinking ? '#ff1744' : '#b71c1c';
      ctx.fill();
    }

    // 3. Bác Tổ Trưởng: Nón tầm nhìn 90 độ di động
    for (const npc of state.inspectors) {
      const sc = this.camera.worldToScreen({ x: npc.x, y: npc.y });
      const r = npc.range * this.camera.zoom;
      const startAngle = npc.facingAngle - npc.fov / 2;
      const endAngle = npc.facingAngle + npc.fov / 2;

      ctx.beginPath();
      ctx.moveTo(sc.x, sc.y);
      ctx.arc(sc.x, sc.y, r, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = GAME_CONFIG.render.colors.visionCone;
      ctx.fill();
      ctx.strokeStyle = 'rgba(239, 83, 80, 0.6)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // 4. Thánh Mẫu Đạo Đức (Bảo Ngọc): Nón tầm nhìn giám sát 120 độ màu xanh ngọc
    for (const walker of state.dummyWalkers) {
      if (walker.archetype === 'altruist') {
        const sc = this.camera.worldToScreen({ x: walker.x, y: walker.y });
        const r = GAME_CONFIG.gameTheory.dummyWalkers.altruistVisionRange * this.camera.zoom;
        const fov = GAME_CONFIG.gameTheory.dummyWalkers.altruistFovRad;
        const startAngle = walker.facingAngle - fov / 2;
        const endAngle = walker.facingAngle + fov / 2;

        ctx.beginPath();
        ctx.moveTo(sc.x, sc.y);
        ctx.arc(sc.x, sc.y, r, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = 'rgba(0, 230, 118, 0.12)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(0, 230, 118, 0.45)';
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }
    }
  }

  private renderPoops(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    for (const p of state.poops) {
      if (p.isCleaned) continue;
      const sc = this.camera.worldToScreen({ x: p.x, y: p.y });

      ctx.font = `${Math.max(14, 16 * (this.camera.zoom / 4))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('💩', sc.x, sc.y);

      // Hiệu ứng bốc mùi (stink vapor)
      ctx.strokeStyle = 'rgba(139, 195, 74, 0.6)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sc.x - 3, sc.y - 10);
      ctx.quadraticCurveTo(sc.x - 5, sc.y - 16, sc.x - 2, sc.y - 20);
      ctx.moveTo(sc.x + 3, sc.y - 10);
      ctx.quadraticCurveTo(sc.x + 5, sc.y - 16, sc.x + 2, sc.y - 20);
      ctx.stroke();
    }
  }

  private renderFootprints(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    for (const fp of state.footprints) {
      const sc = this.camera.worldToScreen({ x: fp.x, y: fp.y });
      ctx.fillStyle = `rgba(109, 76, 65, ${fp.opacity})`;
      ctx.beginPath();
      ctx.ellipse(sc.x, sc.y, 4, 2, fp.angle, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderLeash(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const isMoving = state.player.targetMove !== null;
    const bounceOffset = isMoving ? Math.sin(performance.now() / 100) * 3 : 0;

    const pSc = this.camera.worldToScreen({
      x: state.player.x,
      y: state.player.y,
    });
    const dSc = this.camera.worldToScreen({
      x: state.dog.x,
      y: state.dog.y,
    });

    const dist = Math.hypot(
      state.dog.x - state.player.x,
      state.dog.y - state.player.y
    );
    const tensionRatio = dist / GAME_CONFIG.dog.maxLeashLength;

    // Màu dây xích chuyển từ vàng sang đỏ khi bị căng
    let leashColor = '#ffb300';
    if (tensionRatio > 0.85) leashColor = '#ff3d00';
    else if (tensionRatio > 0.65) leashColor = '#ffa000';

    ctx.beginPath();
    ctx.moveTo(pSc.x, pSc.y);

    // Catenary dip (dây xích đung đưa đàn hồi khi chạy)
    const midX = (pSc.x + dSc.x) / 2;
    const midY =
      (pSc.y + dSc.y) / 2 +
      Math.max(0, (1 - tensionRatio) * 12) +
      bounceOffset;
    ctx.quadraticCurveTo(midX, midY, dSc.x, dSc.y);

    ctx.strokeStyle = leashColor;
    ctx.lineWidth = Math.max(2, 2.5 * (this.camera.zoom / 4));
    ctx.stroke();
  }

  private renderPlayer(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const isMoving = state.player.targetMove !== null;
    const bob = isMoving ? Math.abs(Math.sin(performance.now() / 140)) * 5 : 0;

    const sc = this.camera.worldToScreen({
      x: state.player.x,
      y: state.player.y,
    });
    const r = 1.0 * this.camera.zoom;

    // Bóng đổ nhân vật (nhỏ lại khi nhấc chân)
    ctx.beginPath();
    ctx.arc(sc.x + 2, sc.y + 2, Math.max(2, r - bob * 0.4), 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.fill();

    // Chân bước đi (hai bàn chân stepping animation)
    if (isMoving) {
      const stepPhase = Math.sin(performance.now() / 140);
      const perpAngle = state.player.facingAngle + Math.PI / 2;
      const f1x = sc.x + Math.cos(perpAngle) * 5 + Math.cos(state.player.facingAngle) * stepPhase * 7;
      const f1y = sc.y + Math.sin(perpAngle) * 5 + Math.sin(state.player.facingAngle) * stepPhase * 7;
      const f2x = sc.x - Math.cos(perpAngle) * 5 - Math.cos(state.player.facingAngle) * stepPhase * 7;
      const f2y = sc.y - Math.sin(perpAngle) * 5 - Math.sin(state.player.facingAngle) * stepPhase * 7;

      ctx.fillStyle = '#37474f';
      ctx.beginPath();
      ctx.arc(f1x, f1y, 3, 0, Math.PI * 2);
      ctx.arc(f2x, f2y, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Thân nhân vật nhún lên theo nhịp bước
    const bodyY = sc.y - bob;
    ctx.beginPath();
    ctx.arc(sc.x, bodyY, r, 0, Math.PI * 2);
    ctx.fillStyle = state.player.isEntangled ? '#ff5252' : '#29b6f6';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Hướng nhìn
    const hx = sc.x + Math.cos(state.player.facingAngle) * r * 1.3;
    const hy = bodyY + Math.sin(state.player.facingAngle) * r * 1.3;
    ctx.beginPath();
    ctx.moveTo(sc.x, bodyY);
    ctx.lineTo(hx, hy);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Nhãn Player
    ctx.font = 'bold 11px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.fillText('🧍 Bạn', sc.x, bodyY - r - 6);

    // Trạng thái trói chân do chó quấn xích
    if (state.player.isEntangled) {
      ctx.font = 'bold 10px sans-serif';
      ctx.fillStyle = '#ff1744';
      ctx.fillText('⛓️ BỊ TRÓI CHÂN!', sc.x, bodyY - r - 18);
    }
  }

  private renderDog(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const isMoving =
      state.player.targetMove !== null &&
      !state.dog.isPooping &&
      Math.hypot(state.player.x - state.dog.x, state.player.y - state.dog.y) > 0.8;

    // Nhịp chạy lon ton nhanh và nhún nhảy
    const bob = isMoving ? Math.abs(Math.sin(performance.now() / 90)) * 6 : 0;
    const wiggle = isMoving
      ? Math.sin(performance.now() / 100) * 0.2
      : Math.sin(performance.now() / 350) * 0.08;

    const sc = this.camera.worldToScreen({
      x: state.dog.x,
      y: state.dog.y,
    });
    const r = 0.85 * this.camera.zoom;

    // Bóng đổ chó
    ctx.beginPath();
    ctx.arc(sc.x + 1.5, sc.y + 1.5, Math.max(2, r - bob * 0.5), 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.fill();

    // 4 Chân cún lon ton chạy bước
    if (isMoving) {
      const trot = Math.sin(performance.now() / 90);
      const pAngle = state.dog.facingAngle + Math.PI / 2;
      const legDist = 5;

      ctx.fillStyle = '#d7ccc8';
      // Chân trước
      ctx.beginPath();
      ctx.arc(
        sc.x + Math.cos(pAngle) * legDist + Math.cos(state.dog.facingAngle) * trot * 6,
        sc.y + Math.sin(pAngle) * legDist + Math.sin(state.dog.facingAngle) * trot * 6,
        2.5,
        0,
        Math.PI * 2
      );
      ctx.arc(
        sc.x - Math.cos(pAngle) * legDist - Math.cos(state.dog.facingAngle) * trot * 6,
        sc.y - Math.sin(pAngle) * legDist - Math.sin(state.dog.facingAngle) * trot * 6,
        2.5,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }

    const dogCenterY = sc.y - bob;

    ctx.save();
    ctx.translate(sc.x, dogCenterY);
    ctx.rotate(state.dog.facingAngle + wiggle);

    // Thân chó Poodle lông xù màu vàng
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fillStyle = state.dog.isPooping ? '#ff7043' : '#ffb74d';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Đuôi cún vẫy tít mù
    ctx.beginPath();
    ctx.moveTo(-r * 0.8, 0);
    ctx.lineTo(-r * 1.5, Math.sin(performance.now() / 70) * 6);
    ctx.strokeStyle = '#ffa726';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Mắt và tai cún nhìn về phía trước
    ctx.fillStyle = '#5d4037';
    ctx.beginPath();
    ctx.arc(r * 0.4, -r * 0.4, 2, 0, Math.PI * 2);
    ctx.arc(r * 0.4, r * 0.4, 2, 0, Math.PI * 2);
    ctx.fill();

    // Mũi cún màu đen
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(r * 0.8, 0, 2.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();

    // Bubble chó kêu / sủa / mót
    if (state.dog.isPooping) {
      this.drawSpeechBubble(sc.x, dogCenterY - r - 12, 'Đang xả... 😣', '#ff7043');
    } else if (state.dog.isBarking) {
      this.drawSpeechBubble(sc.x, dogCenterY - r - 12, 'GÂU GÂU! 🚨', '#f44336');
    } else if (state.dog.bowels >= 90) {
      this.drawSpeechBubble(sc.x, dogCenterY - r - 12, 'Mót quá rồi! 💦', '#ffa726');
    }
  }

  private renderInspectors(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const zoomRatio = this.camera.zoom / this.camera.baseZoom;
    for (const npc of state.inspectors) {
      const sc = this.camera.worldToScreen({ x: npc.x, y: npc.y });
      const r = 0.9 * this.camera.zoom;

      ctx.beginPath();
      ctx.arc(sc.x, sc.y, r, 0, Math.PI * 2);
      ctx.fillStyle = '#e53935';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.font = `${Math.max(14, 16 * (this.camera.zoom / 4))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🧹', sc.x, sc.y);

      // Tên Bác Tổ Trưởng chỉ hiện khi zoom in (>= 1.25x)
      if (zoomRatio >= 1.25) {
        ctx.font = 'bold 10px sans-serif';
        ctx.fillStyle = '#ef9a9a';
        ctx.fillText('Bác Tổ Trưởng', sc.x, sc.y - r - 6);
      }

      // Bong bóng thoại của Bác Tổ Trưởng nếu có
      if (npc.speechText) {
        this.drawSpeechBubble(sc.x, sc.y - r - 16, npc.speechText, '#c62828');
      }
    }
  }

  private renderTeaStall(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const zoomRatio = this.camera.zoom / this.camera.baseZoom;
    for (const stall of state.teaStalls) {
      const sc = this.camera.worldToScreen({ x: stall.x, y: stall.y });

      // Ô che nắng quán nước
      ctx.font = `${Math.max(20, 24 * (this.camera.zoom / 4))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⛱️☕', sc.x, sc.y);

      // Tên quán nước chỉ hiện khi zoom in (>= 1.25x)
      if (zoomRatio >= 1.25) {
        ctx.font = 'bold 11px sans-serif';
        ctx.fillStyle = '#ffab91';
        ctx.fillText('Trà Đá Cô Hoa', sc.x, sc.y + 16);
      }

      // Lời cà khịa của quán nước (nâng cao hơn để không đè lên mái quán và nhà bên)
      if (stall.activeComment) {
        this.drawSpeechBubble(sc.x, sc.y - 32, stall.activeComment, '#ff7043');
      }
    }
  }

  private renderQTEAim(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const pSc = this.camera.worldToScreen({
      x: state.player.x,
      y: state.player.y,
    });
    const tSc = this.camera.worldToScreen(state.qteKick.targetDoorPos);

    ctx.beginPath();
    ctx.moveTo(pSc.x, pSc.y);
    ctx.lineTo(tSc.x, tSc.y);
    ctx.strokeStyle = '#76ff03';
    ctx.lineWidth = 2.5;
    ctx.setLineDash([6, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.font = 'bold 12px sans-serif';
    ctx.fillStyle = '#76ff03';
    ctx.textAlign = 'center';
    ctx.fillText('🎯 HƯỚNG SÚT ĐỔ TỘI', tSc.x, tSc.y - 8);
  }

  private drawSpeechBubble(
    x: number,
    y: number,
    text: string,
    bgColor: string
  ): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.font = 'bold 11px sans-serif';
    const textWidth = ctx.measureText(text).width;
    const padX = 8;
    const padY = 5;
    const boxHeight = 22;
    const boxWidth = textWidth + padX * 2;

    ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 2;

    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.roundRect(x - boxWidth / 2, y - boxHeight, boxWidth, boxHeight, 8);
    ctx.fill();

    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x, y - boxHeight / 2);
    ctx.restore();
  }

  private renderDummyWalkers(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const zoomRatio = this.camera.zoom / this.camera.baseZoom;

    for (const walker of state.dummyWalkers) {
      const wSc = this.camera.worldToScreen({ x: walker.x, y: walker.y });
      const r = 1.15 * this.camera.zoom;

      // 0. Soft drop shadow (Chuẩn Chibi BaChuViaHe)
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.38)';
      ctx.beginPath();
      ctx.ellipse(wSc.x, wSc.y + r * 0.75, r * 1.1, r * 0.42, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // 0.5. Đèn pin / Ánh sáng rọi (Flashlight cone) cho Bà Tám hoặc Dân Phòng
      if (walker.hasFlashlight || walker.archetype === 'ba_tam' || walker.archetype === 'dan_phong') {
        const flashR = 18 * this.camera.zoom;
        const fov = Math.PI / 3;
        const grad = ctx.createRadialGradient(wSc.x, wSc.y, 2, wSc.x, wSc.y, flashR);
        grad.addColorStop(0, 'rgba(255, 235, 59, 0.45)');
        grad.addColorStop(1, 'rgba(255, 235, 59, 0)');
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(wSc.x, wSc.y);
        ctx.arc(wSc.x, wSc.y, flashR, walker.facingAngle - fov / 2, walker.facingAngle + fov / 2);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.restore();
      }

      // 1. Dây xích & Chó (nếu có cún đi kèm)
      if (walker.dog) {
        const dSc = this.camera.worldToScreen({ x: walker.dog.x, y: walker.dog.y });
        ctx.beginPath();
        ctx.moveTo(wSc.x, wSc.y);
        const midX = (wSc.x + dSc.x) / 2;
        const midY = (wSc.y + dSc.y) / 2 + 5;
        ctx.quadraticCurveTo(midX, midY, dSc.x, dSc.y);
        ctx.strokeStyle =
          walker.archetype === 'altruist' || walker.archetype === 'ba_tam'
            ? '#f48fb1'
            : walker.archetype === 'defector'
            ? '#ff6d00'
            : '#ffd600';
        ctx.lineWidth = Math.max(1.5, 2 * (this.camera.zoom / 4));
        ctx.stroke();

        // Chó của Dummy Walker
        const dogR = 0.8 * this.camera.zoom;
        ctx.beginPath();
        ctx.arc(dSc.x, dSc.y, dogR, 0, Math.PI * 2);
        ctx.fillStyle =
          walker.archetype === 'altruist' || walker.archetype === 'ba_tam'
            ? '#ffffff'
            : walker.archetype === 'defector'
            ? '#ffb74d'
            : '#5d4037';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Icon chó
        let dogIcon = '🐕';
        if (walker.archetype === 'altruist' || walker.archetype === 'ba_tam') dogIcon = '🐩🎀';
        else if (walker.archetype === 'tit_for_tat') dogIcon = '🐶';

        ctx.font = `${Math.max(12, 14 * (this.camera.zoom / 4))}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(dogIcon, dSc.x, dSc.y);

        if (walker.dog.isPooping) {
          ctx.font = '12px sans-serif';
          ctx.fillText('💩', dSc.x, dSc.y - dogR - 8);
        }
      }

      // 2. Thân người Chibi Dummy Walker (Đầu to, tròn, dễ thương)
      let bodyColor = '#0288d1';
      let personIcon = '🧍';

      switch (walker.archetype) {
        case 'ba_tam':
          bodyColor = '#e91e63';
          personIcon = '👵';
          break;
        case 'bac_huu_tri':
          bodyColor = '#795548';
          personIcon = '👴';
          break;
        case 'gen_z':
          bodyColor = '#9c27b0';
          personIcon = '📱';
          break;
        case 'dan_phong':
          bodyColor = '#1565c0';
          personIcon = '👮‍♂️';
          break;
        case 'nguoi_tho_o':
          bodyColor = '#607d8b';
          personIcon = '🎧';
          break;
        case 'altruist':
          bodyColor = '#ec407a';
          personIcon = '👩‍💼';
          break;
        case 'defector':
          bodyColor = '#ff5722';
          personIcon = '🏃‍♂️';
          break;
        case 'tit_for_tat':
          bodyColor = '#0288d1';
          personIcon = '🧔';
          break;
      }

      ctx.beginPath();
      ctx.arc(wSc.x, wSc.y, r, 0, Math.PI * 2);
      ctx.fillStyle = bodyColor;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2.2;
      ctx.stroke();

      // Vẽ Icon Chibi người
      ctx.font = `${Math.max(15, 17 * (this.camera.zoom / 4))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(personIcon, wSc.x, wSc.y);

      // Phụ kiện Gen Z quay TikTok 4K
      if (walker.hasPhone || walker.archetype === 'gen_z') {
        ctx.save();
        ctx.font = 'bold 9px monospace';
        ctx.fillStyle = '#ff1744';
        ctx.textAlign = 'left';
        ctx.fillText('🔴 REC', wSc.x + r + 2, wSc.y - r + 4);
        ctx.restore();
      }

      // Nhãn tên và Archetype (chỉ hiện khi zoom in vừa phải)
      if (zoomRatio >= 1.15) {
        ctx.font = 'bold 10px sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.fillText(walker.name, wSc.x, wSc.y - r - 6);
      }

      // Bong bóng thoại Comic nếu có
      if (walker.speechText) {
        this.drawSpeechBubble(
          wSc.x,
          wSc.y - r - 18,
          walker.speechText,
          walker.speechColor || '#37474f'
        );
      }
    }
  }

  private renderSoundWaves(state: Readonly<GameState>): void {
    if (!state.soundWaves || state.soundWaves.length === 0) return;
    const ctx = this.ctx;

    for (const wave of state.soundWaves) {
      const sc = this.camera.worldToScreen({ x: wave.x, y: wave.y });
      const rPx = wave.radius * this.camera.zoom;
      const progress = Math.min(1, wave.radius / wave.maxRadius);
      const alpha = Math.max(0, 1 - progress);

      ctx.save();
      ctx.beginPath();
      ctx.arc(sc.x, sc.y, rPx, 0, Math.PI * 2);
      ctx.strokeStyle = wave.color || '#ff1744';
      ctx.lineWidth = Math.max(1.5, 3.5 * alpha);
      ctx.globalAlpha = alpha * 0.8;
      ctx.stroke();

      // Sóng phụ bên trong
      if (rPx > 15) {
        ctx.beginPath();
        ctx.arc(sc.x, sc.y, rPx * 0.7, 0, Math.PI * 2);
        ctx.lineWidth = Math.max(1, 2 * alpha);
        ctx.globalAlpha = alpha * 0.45;
        ctx.stroke();
      }
      ctx.restore();

      // Chữ "TRI HÔ!" lúc mới phát sóng
      if (wave.radius < 8) {
        ctx.save();
        ctx.font = 'bold 11px sans-serif';
        ctx.fillStyle = '#ff1744';
        ctx.textAlign = 'center';
        ctx.fillText('📢 TRI HÔ! ỐI LÀNG NƯỚC ƠI!', sc.x, sc.y - rPx - 8);
        ctx.restore();
      }
    }
  }

  private renderChickenGameFx(state: Readonly<GameState>): void {
    const ctx = this.ctx;
    const playerDog = state.dog;
    const pSc = this.camera.worldToScreen({ x: playerDog.x, y: playerDog.y });

    for (const walker of state.dummyWalkers) {
      if (walker.inChickenGameWithPlayer && walker.dog) {
        const dSc = this.camera.worldToScreen({ x: walker.dog.x, y: walker.dog.y });
        const midX = (pSc.x + dSc.x) / 2;
        const midY = (pSc.y + dSc.y) / 2;

        // Tia lửa / sấm sét đối đầu
        const pulse = (Math.sin(performance.now() / 80) + 1) / 2;
        ctx.font = '20px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('⚡💢⚡', midX, midY - 14);

        // Banner Chicken Game
        ctx.save();
        ctx.font = 'bold 11px sans-serif';
        const txt = '🐔 TRÒ CHƠI CON GÀ (CHICKEN GAME)!';
        const tw = ctx.measureText(txt).width;
        ctx.fillStyle = `rgba(255, 23, 68, ${0.8 + pulse * 0.2})`;
        ctx.beginPath();
        ctx.roundRect(midX - tw / 2 - 8, midY - 38, tw + 16, 20, 6);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.fillText(txt, midX, midY - 28);
        ctx.restore();
      }
    }
  }

  public getSnapshot(): string {
    return this.canvas.toDataURL('image/png');
  }
}
